import { useTheme } from 'styled-components';
import {
  FileEvidenceForDataDeletionType,
  RemovePIIReportEvents,
} from 'src/components/pages/Case/Case.slice';
import { DataDeletionType, EventDeletionType } from 'src/graphql';
import { Accordion } from '../../Accordion';
import { SimpleList } from '../../SimpleList';
import { MessagesSummaryList, SummaryWarning } from '../components';
import { getDeletedTextListFromString } from '../helpers';
import { StepperModalHeader } from '../RemovePIIDataModal.styles';
import {
  ImageContainer,
  SummaryContentContainer,
  SummaryContentGrid,
  SummaryNestedAccordionContainer,
} from './SummaryStep.styles';

interface SummaryStepProps {
  reportId: string;
  payload: DataDeletionType;
  reportEvents: RemovePIIReportEvents[];
  reporterNotes?: string | null;
  postSubmissionFiles?: FileEvidenceForDataDeletionType[];
}

export const SummaryStep = ({
  reportId,
  payload,
  reportEvents,
  reporterNotes,
  postSubmissionFiles,
}: SummaryStepProps) => {
  const theme = useTheme();
  const emptyStateSrc = theme.isVaultVariant
    ? '/assets/empty-state.svg'
    : '/assets/empty-state-atlas.svg';
  const getNamedOffendersDeleted = (event: RemovePIIReportEvents) => {
    const namedOffenders = event?.namedOffenders || [];
    const selectedNamedOffenderIds = [
      ...payload.preInternalNamedOffendersIds,
      ...payload.preExternalNamedOffendersIds,
      ...payload.postInternalNamedOffendersIds,
      ...payload.postExternalNamedOffendersIds,
    ];
    const offendersToDelete = namedOffenders
      .filter((offender) => selectedNamedOffenderIds.includes(offender.id))
      .map((offender) => offender.name);
    if (
      event?.nonSeparatedExternalNamedOffenders.length === 1 &&
      payload.externalNamedOffenders.length
    ) {
      event.nonSeparatedExternalNamedOffenders.forEach((offender) => {
        const redactions = getDeletedTextListFromString(
          payload.externalNamedOffenders,
          offender.name,
        );
        offendersToDelete.push(...redactions);
      });
    }

    return offendersToDelete;
  };

  const hasEventDeletions =
    payload.eventDeletions && payload.eventDeletions.length > 0;
  const hasLegacyDeletions =
    payload.description.length > 0 ||
    payload.emotion.length > 0 ||
    payload.witnesses.length > 0 ||
    payload.location.length > 0 ||
    payload.preSubmissionS3Paths.length > 0 ||
    payload.externalNamedOffenders.length > 0 ||
    reportEvents.some(
      (reportEvent) => getNamedOffendersDeleted(reportEvent).length > 0,
    );
  const hasPostSubmissionFiles = () => {
    return postSubmissionFiles && postSubmissionFiles.length > 0;
  };

  const emptyStateWithImage = () => (
    <SummaryContentContainer>
      <SummaryWarning warningText="No data selected for removal" warningState />
      <ImageContainer>
        <img src={emptyStateSrc} alt="Empty state" width="100%" />
      </ImageContainer>
    </SummaryContentContainer>
  );

  const hasSelectedReporterInformation =
    payload.anonymiseReporter || payload.reporterNotes.length > 0;

  const hasSelectedMessages = payload.messages && payload.messages.length > 0;

  const hasSelectedPostSubmissionFiles =
    payload.postSubmissionS3Paths.length > 0;

  const getPreSubmissionEvidenceDeleted = (event: RemovePIIReportEvents) => {
    const evidenceWithNames = event?.files
      ?.filter((file) => file.isPreSubmission && file.dataDeletedAt === null)
      .map((file, i) => ({
        ...file,
        name: `EvidenceUploaded${i + 1}.${file.fileExtension}`,
      }));

    const s3PathsSelectedForDeletion = hasEventDeletions
      ? [
          ...(payload.eventDeletions?.find(
            (eventDeletion) => eventDeletion.eventId === event.event,
          )?.preSubmissionS3Paths ?? []),
        ]
      : [...payload.preSubmissionS3Paths];
    const evidenceToDelete =
      evidenceWithNames
        ?.filter((file) => s3PathsSelectedForDeletion.includes(file.fileS3Path))
        .map((file) => file.name) || [];

    return evidenceToDelete;
  };

  const getPostSubmissionEvidenceDeleted = (
    files: FileEvidenceForDataDeletionType[],
  ) => {
    const postSubmissionFiles = files.filter(
      (file) => file.dataDeletedAt === null,
    );

    const evidenceWithNames = postSubmissionFiles.map((file, i) => ({
      ...file,
      name: `AdditionalEvidence${i + 1}.${file.fileExtension}`,
    }));

    const s3PathsSelectedForDeletion = [...payload.postSubmissionS3Paths];
    const evidenceToDelete =
      evidenceWithNames
        ?.filter((file) => s3PathsSelectedForDeletion.includes(file.fileS3Path))
        .map((file) => file.name) || [];

    return evidenceToDelete;
  };

  const getEvidenceDeletedPreSubmission = (events: RemovePIIReportEvents[]) => {
    const deletionEvidence: string[] = events
      .map((eventItem) => {
        return getPreSubmissionEvidenceDeleted(eventItem);
      })
      .flat();

    return deletionEvidence;
  };

  const getEvidenceDeletedPostSubmission = (
    postSubmissionFiles: FileEvidenceForDataDeletionType[],
  ) => {
    const evidenceWithNames =
      getPostSubmissionEvidenceDeleted(postSubmissionFiles);
    return evidenceWithNames;
  };

  const deletedNamedOffenders = [
    ...new Set(
      reportEvents.flatMap((reportEvent) =>
        getNamedOffendersDeleted(reportEvent),
      ),
    ),
  ];

  return (
    <>
      <StepperModalHeader data-testid="remove-pii-data-modal-header">
        Review your deletion
      </StepperModalHeader>

      <Accordion
        topBorderRadius="6px"
        bottomBorderRadius="0"
        title="Step 1 - Reporter information"
        bodyBackgroundColor={theme.atlas.semantic.color.surface.subtle}
        topBorder
        data-testid="reporter-information"
      >
        {hasSelectedReporterInformation ? (
          <SummaryContentContainer>
            <SummaryWarning
              data-testid="reporter-information-summary-warning"
              warningText="The following items will be anonymised or deleted"
            />
            <SummaryContentGrid>
              <SimpleList
                title="Reporter information"
                subtitle={
                  payload.anonymiseReporter
                    ? { value: 'Anonymised', type: 'warning' }
                    : undefined
                }
                content={
                  payload.anonymiseReporter
                    ? [
                        'Reporter name',
                        'Email',
                        'Role',
                        'Employee ID',
                        'Department',
                        'Site',
                        'Country',
                        'Notification email',
                      ]
                    : []
                }
                data-testid="reporter-anonymised-list"
              />
              <SimpleList
                title="Reporter notes"
                subtitle={
                  payload.reporterNotes.length
                    ? { value: 'Deleted', type: 'warning' }
                    : undefined
                }
                content={getDeletedTextListFromString(
                  payload.reporterNotes,
                  reporterNotes || '',
                )}
                data-testid="reporter-notes-list"
              />
            </SummaryContentGrid>
          </SummaryContentContainer>
        ) : (
          emptyStateWithImage()
        )}
      </Accordion>
      <Accordion
        title="Step 2 - Report details"
        bodyBackgroundColor={theme.atlas.semantic.color.surface.subtle}
        topBorderRadius="0"
        bottomBorderRadius="0"
        data-testid="report-details"
      >
        {hasLegacyDeletions && (
          <SummaryContentContainer>
            <SummaryWarning data-testid="report-details-summary-warning" />
            <SummaryContentGrid>
              <SimpleList
                title="Named offender/s"
                content={deletedNamedOffenders}
                data-testid="named-offenders-list"
              />
              {!hasEventDeletions && (
                <SimpleList
                  title="Evidence"
                  content={getEvidenceDeletedPreSubmission(reportEvents)}
                  data-testid="evidence-list"
                />
              )}
            </SummaryContentGrid>
          </SummaryContentContainer>
        )}
        {hasEventDeletions && (
          <SummaryNestedAccordionContainer>
            {payload.eventDeletions?.map(
              (eventDeletion: EventDeletionType, idx: number) => {
                const event = reportEvents.find(
                  (e) => e.event === eventDeletion.eventId,
                );
                if (!event) return null;
                return (
                  <Accordion
                    key={event.event}
                    title={`Event ID: ${event.eventId}`}
                    bottomBorderRadius="0"
                    topBorderRadius={idx === 0 ? '6px' : '0'}
                    data-testid={`${event.eventId}-report-details-event-accordion`}
                  >
                    <SummaryContentContainer>
                      <SummaryWarning
                        data-testid="report-details-event-summary-warning"
                        eventId={
                          reportEvents.find(
                            (event) => event.event === eventDeletion.eventId,
                          )?.eventId
                        }
                      />
                      <SummaryContentGrid>
                        <SimpleList
                          title="Event location"
                          content={getDeletedTextListFromString(
                            eventDeletion.location,
                            event.evidence?.location?.value?.toString() || '',
                          )}
                          data-testid={`event-location-list-${eventDeletion.eventId}`}
                        />
                        <SimpleList
                          title="What happened"
                          content={getDeletedTextListFromString(
                            eventDeletion.description,
                            event.evidence?.description?.value?.toString() ||
                              '',
                          )}
                          data-testid={`what-happened-list-${eventDeletion.eventId}`}
                        />
                        <SimpleList
                          title="Emotional impact"
                          content={getDeletedTextListFromString(
                            eventDeletion.emotion,
                            event.evidence?.emotion?.value?.toString() || '',
                          )}
                          data-testid={`emotional-impact-list-${eventDeletion.eventId}`}
                        />
                        <SimpleList
                          title="Witnesses"
                          content={getDeletedTextListFromString(
                            eventDeletion.witnesses,
                            event.evidence?.witness?.value?.toString() || '',
                          )}
                          data-testid={`witnesses-list-${eventDeletion.eventId}`}
                        />
                        {event.nonSeparatedExternalNamedOffenders.length >
                          0 && (
                          <SimpleList
                            title="Named offender/s"
                            content={(payload.eventDeletions ?? [])?.flatMap(
                              (eventDeletion, idx) => {
                                return eventDeletion.externalNamedOffenders
                                  ? getDeletedTextListFromString(
                                      eventDeletion.externalNamedOffenders,
                                      event.nonSeparatedExternalNamedOffenders[
                                        idx
                                      ].name,
                                    )
                                  : [];
                              },
                            )}
                            data-testid={`named-offenders-list-${eventDeletion.eventId}`}
                          />
                        )}
                        <SimpleList
                          title="Evidence"
                          content={getEvidenceDeletedPreSubmission([event])}
                          data-testid={`evidence-list-${eventDeletion.eventId}`}
                        />
                      </SummaryContentGrid>
                    </SummaryContentContainer>
                  </Accordion>
                );
              },
            )}
          </SummaryNestedAccordionContainer>
        )}
        {!hasEventDeletions && !hasLegacyDeletions && emptyStateWithImage()}
      </Accordion>
      <Accordion
        bottomBorderRadius={hasPostSubmissionFiles() ? '0' : '6px'}
        topBorderRadius="0"
        title="Step 3 - Messages"
        last
        bodyBackgroundColor={theme.atlas.semantic.color.surface.subtle}
        data-testid="messages"
      >
        {hasSelectedMessages ? (
          <SummaryContentContainer>
            <SummaryWarning data-testid="messages-summary-warning" />
            {payload.messages.length > 0 && (
              <MessagesSummaryList
                reportId={reportId}
                selectedMessageIds={payload.messages}
              />
            )}
          </SummaryContentContainer>
        ) : (
          emptyStateWithImage()
        )}
      </Accordion>
      {hasPostSubmissionFiles() && (
        <Accordion
          title="Post submission evidence"
          bodyBackgroundColor={theme.atlas.semantic.color.surface.subtle}
          topBorderRadius="0"
          bottomBorderRadius="6px"
          data-testid="post-submission-evidence-summary"
        >
          {hasSelectedPostSubmissionFiles ? (
            <SummaryContentContainer>
              <SummaryWarning data-testid="post-submission-evidence-summary-warning" />
              <SummaryContentGrid>
                <SimpleList
                  title="Evidence"
                  content={getEvidenceDeletedPostSubmission(
                    postSubmissionFiles ?? [],
                  )}
                  data-testid="post-submission-evidence-list"
                />
              </SummaryContentGrid>
            </SummaryContentContainer>
          ) : (
            emptyStateWithImage()
          )}
        </Accordion>
      )}
    </>
  );
};
